import {
  Briefcase,
  Building2,
  ChevronRight,
  CircleDot,
  ContactRound,
  Settings,
  SquareCheck,
  Tag,
  UserRound,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import {
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { PAGE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import { cn } from "@/lib/utils/cn";
import { Link } from "@tanstack/react-router";

const ALL_REFERRALS_PATH = "/referrals";
const PARTNER_FIRMS_PATH = "/referrals/partner-firms";
const DIRECTORY_PROFILE_PATH = "/referrals/directory-profile";
const REFERRAL_SETTINGS_PATH = "/referrals/settings";
const ROLES_SETTINGS_PATH = "/referrals/settings/roles";
const TEAMS_SETTINGS_PATH = "/referrals/settings/teams";
const INCIDENT_TYPES_PATH = "/referrals/settings/matter-types";
const STATUSES_PATH = "/referrals/settings/statuses";
const TAGS_PATH = "/referrals/settings/tags";
const TASK_PLANS_PATH = "/referrals/settings/task-plans";

const referralSubNavLabelClass =
  "[&>span:last-child]:min-w-0 [&>span:last-child]:whitespace-nowrap [&>span:last-child]:overflow-hidden [&>span:last-child]:text-ellipsis";

type ReferralNestedMenuItem = {
  title: string;
  url: string;
  icon: LucideIcon;
};

const isReferralSettingsPath = (path: string) =>
  path.startsWith(REFERRAL_SETTINGS_PATH);

export const ReferralsSubMenu = ({ currentPath }: { currentPath: string }) => {
  const { setOpenMobile } = useSidebar();
  const { hasPagePermission } = useUser();
  const [settingsOpen, setSettingsOpen] = useState(() =>
    isReferralSettingsPath(currentPath),
  );
  const prevPathRef = useRef(currentPath);

  useEffect(() => {
    const prev = prevPathRef.current;
    prevPathRef.current = currentPath;
    if (!isReferralSettingsPath(prev) && isReferralSettingsPath(currentPath)) {
      setSettingsOpen(true);
    }
  }, [currentPath]);

  const canViewAllReferrals = hasPagePermission(
    PAGE_PERMISSION_KEYS.referralsAll,
  );
  const canViewDirectoryProfile = hasPagePermission(
    PAGE_PERMISSION_KEYS.referralsSettings,
  );
  const canViewMatterRoles = hasPagePermission(
    PAGE_PERMISSION_KEYS.referralsSettingsRoles,
  );
  const canViewMatterTeams = hasPagePermission(
    PAGE_PERMISSION_KEYS.referralsSettingsTeams,
  );
  const canViewIncidentTypes = hasPagePermission(
    PAGE_PERMISSION_KEYS.referralsSettingsTypes,
  );
  const canViewStatuses = hasPagePermission(
    PAGE_PERMISSION_KEYS.referralsSettingsStatuses,
  );
  const canViewTags = hasPagePermission(
    PAGE_PERMISSION_KEYS.referralsSettingsTags,
  );
  const canViewTaskPlans = hasPagePermission(
    PAGE_PERMISSION_KEYS.referralsSettingsTaskPlans,
  );

  const referralSettingsItems: ReferralNestedMenuItem[] = [
    canViewMatterRoles
      ? { title: "Roles", url: ROLES_SETTINGS_PATH, icon: UserRound }
      : null,
    canViewMatterTeams
      ? { title: "Teams", url: TEAMS_SETTINGS_PATH, icon: Users }
      : null,
    canViewIncidentTypes
      ? { title: "Incident types", url: INCIDENT_TYPES_PATH, icon: Briefcase }
      : null,
    canViewStatuses
      ? { title: "Statuses", url: STATUSES_PATH, icon: CircleDot }
      : null,
    canViewTags ? { title: "Tags", url: TAGS_PATH, icon: Tag } : null,
    canViewTaskPlans
      ? { title: "Task plans", url: TASK_PLANS_PATH, icon: SquareCheck }
      : null,
  ].filter((item): item is ReferralNestedMenuItem => Boolean(item));

  const canViewReferralSettingsSection = referralSettingsItems.length > 0;
  const isAllReferralsActive =
    currentPath === ALL_REFERRALS_PATH ||
    currentPath === `${ALL_REFERRALS_PATH}/`;

  return (
    <SidebarMenuSub className="mt-1.5 mr-0 ml-2 gap-1 border-l-2 border-l-neutral-900 py-0.5 pr-0 pl-3">
      {canViewAllReferrals ? (
        <>
          <SidebarMenuSubItem>
            <SidebarMenuSubButton asChild className={referralSubNavLabelClass}>
              <Link
                to={ALL_REFERRALS_PATH}
                className={cn(
                  isAllReferralsActive &&
                    "bg-neutral-900 text-white hover:bg-neutral-700 active:bg-neutral-700",
                )}
                onClick={() => {
                  setOpenMobile(false);
                }}
              >
                <Users className="mr-2 h-4 w-4 shrink-0" />
                All referrals
              </Link>
            </SidebarMenuSubButton>
          </SidebarMenuSubItem>
          <SidebarMenuSubItem>
            <SidebarMenuSubButton asChild className={referralSubNavLabelClass}>
              <Link
                to={PARTNER_FIRMS_PATH}
                className={cn(
                  currentPath === PARTNER_FIRMS_PATH &&
                    "bg-neutral-900 text-white hover:bg-neutral-700 active:bg-neutral-700",
                )}
                onClick={() => {
                  setOpenMobile(false);
                }}
              >
                <Building2 className="mr-2 h-4 w-4 shrink-0" />
                Partner firms
              </Link>
            </SidebarMenuSubButton>
          </SidebarMenuSubItem>
        </>
      ) : null}
      {canViewDirectoryProfile ? (
        <SidebarMenuSubItem>
          <SidebarMenuSubButton asChild className={referralSubNavLabelClass}>
            <Link
              to={DIRECTORY_PROFILE_PATH}
              className={cn(
                currentPath === DIRECTORY_PROFILE_PATH &&
                  "bg-neutral-900 text-white hover:bg-neutral-700 active:bg-neutral-700",
              )}
              onClick={() => {
                setOpenMobile(false);
              }}
            >
              <ContactRound className="mr-2 h-4 w-4 shrink-0" />
              Directory profile
            </Link>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      ) : null}
      {canViewReferralSettingsSection ? (
        <SidebarMenuSubItem className="flex flex-col gap-0">
          <SidebarMenuSubButton asChild className={referralSubNavLabelClass}>
            <button
              type="button"
              aria-expanded={settingsOpen}
              className="flex w-full items-center justify-between gap-2"
              onClick={() => {
                setSettingsOpen((open) => !open);
              }}
            >
              <span className="flex min-w-0 flex-1 items-center gap-2">
                <Settings className="h-4 w-4 shrink-0" />
                <span className="min-w-0 truncate text-left">Settings</span>
              </span>
              <ChevronRight
                className={cn(
                  "h-4 w-4 shrink-0 transition-transform",
                  settingsOpen && "rotate-90",
                )}
                aria-hidden
              />
            </button>
          </SidebarMenuSubButton>
          {settingsOpen ? (
            <SidebarMenuSub className="mt-1 mr-0 ml-0 border-l-2 border-l-neutral-300 py-0.5 pr-0 pl-3">
              {referralSettingsItems.map((item) => (
                <SidebarMenuSubItem key={item.url}>
                  <SidebarMenuSubButton
                    asChild
                    className={referralSubNavLabelClass}
                  >
                    <Link
                      to={item.url}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-md",
                        currentPath === item.url ||
                          currentPath.startsWith(`${item.url}/`)
                          ? "bg-neutral-900 text-white hover:bg-neutral-700 active:bg-neutral-700"
                          : "",
                      )}
                      onClick={() => {
                        setOpenMobile(false);
                      }}
                    >
                      <item.icon className="h-4 w-4 shrink-0" aria-hidden />
                      <span className="min-w-0 truncate">{item.title}</span>
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ))}
            </SidebarMenuSub>
          ) : null}
        </SidebarMenuSubItem>
      ) : null}
    </SidebarMenuSub>
  );
};
